import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Phone, Mail } from 'lucide-react';
import ScrollReveal from './ScrollReveal';
import StaggerContainer, { StaggerItem } from './StaggerContainer';

const SimpleFAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: 'Er det gratis?',
      a: 'Ja, helt gratis. Vi tar ingen kostnader for verdivurdering eller formidling.'
    },
    {
      q: 'Hvor lang tid tar det?',
      a: 'Fra du registrerer til pengene er på konto tar det vanligvis 3-5 dager.'
    },
    {
      q: 'Må jeg godta budet?',
      a: 'Nei, det er helt uforpliktende. Du velger selv om du vil akseptere høyeste bud.'
    },
    {
      q: 'Hva skjer med reklamasjonsansvar?',
      a: 'Når bilen er solgt tar kjøper alt ansvar. Du slipper bekymringer.'
    }
  ];

  return (
    <section className="py-20 bg-gray-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-yellow-200 rounded-full blur-3xl opacity-20"></div>
      <div className="absolute bottom-20 left-10 w-64 h-64 bg-yellow-100 rounded-full blur-3xl opacity-15"></div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <ScrollReveal direction="up" className="text-center mb-12">
          <motion.h2 
            className="text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 20
            }}
          >
            Spørsmål?
          </motion.h2>
        </ScrollReveal>

        <StaggerContainer staggerDelay={0.1} className="space-y-4">
          {faqs.map((faq, index) => (
            <StaggerItem key={index}>
              <motion.div 
                className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover-lift"
                whileHover={{ 
                  borderColor: "#fbbf24",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.08)"
                }}
                transition={{ duration: 0.2 }}
              >
                <motion.button
                  className="w-full p-6 text-left flex items-center justify-between group"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  whileTap={{ scale: 0.98 }}
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4 group-hover:text-gray-700 transition-colors">
                    {faq.q}
                  </h3>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ 
                      type: "spring",
                      stiffness: 300,
                      damping: 20
                    }}
                  >
                    <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700 flex-shrink-0 transition-colors" />
                  </motion.div>
                </motion.button>
                
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ 
                        height: "auto", 
                        opacity: 1,
                        transition: {
                          height: { type: "spring", stiffness: 300, damping: 30 },
                          opacity: { duration: 0.2, delay: 0.1 }
                        }
                      }}
                      exit={{ 
                        height: 0, 
                        opacity: 0,
                        transition: {
                          height: { type: "spring", stiffness: 300, damping: 30 },
                          opacity: { duration: 0.1 }
                        }
                      }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-2">
                        <motion.p 
                          className="text-gray-600 leading-relaxed"
                          initial={{ y: -10, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.1 }}
                        >
                          {faq.a}
                        </motion.p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <ScrollReveal direction="up" delay={0.3} className="text-center mt-12">
          <motion.p 
            className="text-gray-600 mb-6 font-medium"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Flere spørsmål?
          </motion.p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a 
              href="tel:47494900" 
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-gray-900 to-gray-700 text-white rounded-xl font-semibold shadow-lg gap-3"
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 15px 35px rgba(0,0,0,0.2)"
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ 
                type: "spring",
                stiffness: 400,
                damping: 17
              }}
            >
              <Phone className="w-5 h-5" />
              Ring oss: 47 49 49 00
            </motion.a>
            <motion.a 
              href="mailto:post@digicar.no" 
              className="inline-flex items-center justify-center px-8 py-4 border-2 text-gray-900 rounded-xl font-semibold gap-3"
              style={{ 
                borderColor: '#fefe94',
                backgroundColor: 'rgba(254, 254, 148, 0.1)'
              }}
              whileHover={{ 
                scale: 1.05,
                backgroundColor: 'rgba(254, 254, 148, 0.2)'
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ 
                type: "spring",
                stiffness: 400,
                damping: 17
              }}
            >
              <Mail className="w-5 h-5" />
              Send e-post
            </motion.a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default SimpleFAQ;